import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Phone, Clock, User, Hash } from 'lucide-react';

const CallHistory = () => {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchHistory();
  }, [statusFilter, user?.exten]);

  const fetchHistory = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (user?.exten) {
        params.bina = user.exten;
      }
      const response = await axios.get(`${API}/call-history`, { params });
      console.log('Call history fetched:', response.data.length, 'entries for exten:', user?.exten);
      setHistory(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
      console.error('Error details:', error.response?.data || error.message);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTimestamp = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      answered: { label: 'Atendida', className: 'bg-green-100 text-green-700' },
      not_answered: { label: 'Não Atendida', className: 'bg-yellow-100 text-yellow-700' },
      busy: { label: 'Ocupado', className: 'bg-orange-100 text-orange-700' },
      voicemail: { label: 'Caixa Postal', className: 'bg-purple-100 text-purple-700' },
      transferred: { label: 'Transferida', className: 'bg-blue-100 text-blue-700' },
    };

    const config = statusConfig[status] || { label: status, className: 'bg-slate-100 text-slate-700' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const getDirectionIcon = (direction) => {
    return direction === 'outbound' ? (
      <div className="text-teal-600" title="Saída">
        ↗️
      </div>
    ) : (
      <div className="text-cyan-600" title="Entrada">
        ↙️
      </div>
    );
  };

  // Stats
  const stats = {
    total: history.length,
    answered: history.filter((h) => h.status === 'answered').length,
    notAnswered: history.filter((h) => h.status === 'not_answered').length,
    totalDuration: history.reduce((sum, h) => sum + h.duration, 0),
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-800 mb-2">Histórico de Chamadas</h1>
        <p className="text-slate-600">Visualize todas as chamadas realizadas e recebidas</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total de Chamadas</p>
                <p className="text-3xl font-bold text-slate-900">{stats.total}</p>
              </div>
              <Phone size={32} className="text-teal-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Atendidas</p>
                <p className="text-3xl font-bold text-green-600">{stats.answered}</p>
              </div>
              <div className="text-4xl">✓</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Não Atendidas</p>
                <p className="text-3xl font-bold text-yellow-600">{stats.notAnswered}</p>
              </div>
              <div className="text-4xl">✗</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Tempo Total</p>
                <p className="text-3xl font-bold text-cyan-600">{formatDuration(stats.totalDuration)}</p>
              </div>
              <Clock size={32} className="text-cyan-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-slate-700">Filtrar por Status:</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-64" data-testid="history-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="answered">Atendidas</SelectItem>
                <SelectItem value="not_answered">Não Atendidas</SelectItem>
                <SelectItem value="busy">Ocupado</SelectItem>
                <SelectItem value="voicemail">Caixa Postal</SelectItem>
                <SelectItem value="transferred">Transferidas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* History Table */}
      <Card>
        <CardHeader>
          <CardTitle>Registro de Chamadas ({history.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Dir.</TableHead>
                  <TableHead>Contato</TableHead>
                  <TableHead>Telefone</TableHead>
                  <TableHead>Bina</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Duração</TableHead>
                  <TableHead>Data/Hora</TableHead>
                  <TableHead>DTMF</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                      Nenhuma chamada registrada ainda
                    </TableCell>
                  </TableRow>
                ) : (
                  history.map((call) => (
                    <TableRow key={call.id} data-testid={`history-row-${call.id}`}>
                      <TableCell>{getDirectionIcon(call.direction)}</TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <User size={14} className="text-slate-400" />
                          {call.contact_name}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{call.phone}</TableCell>
                      <TableCell className="font-mono text-xs text-slate-600">
                        {call.bina || '-'}
                      </TableCell>
                      <TableCell>{getStatusBadge(call.status)}</TableCell>
                      <TableCell className="font-mono text-sm">{formatDuration(call.duration)}</TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {formatTimestamp(call.timestamp)}
                      </TableCell>
                      <TableCell>
                        {call.dtmf_keys ? (
                          <div className="flex items-center gap-1">
                            <Hash size={12} className="text-slate-400" />
                            <span className="font-mono text-xs">{call.dtmf_keys}</span>
                          </div>
                        ) : (
                          <span className="text-slate-400 text-xs">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CallHistory;